import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  RefreshCwIcon,
  SearchIcon,
  StarIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useNavigationType } from 'react-router';

import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { numberText, planLabels } from '@/components/credit-contracts/display';
import { fillViewportSx } from '@/components/credit-contracts/layout';
import { MonthGrid } from '@/components/credit-contracts/MonthPicker';
import {
  compactTableSx,
  fitColumnSx,
  HeaderLabel,
  stickyCell,
  stickyRowSx,
} from '@/components/credit-contracts/tableParts';
import { useContractList } from '@/components/credit-contracts/useContractList';
import type { CompanySummary } from '@/lib/api/credit-contracts';
import { addMonths, formatPeriod, monthLabel } from '@/lib/credit-contracts/month';

/** 最後に契約詳細を開いた企業。ブラウザの「戻る」で契約一覧に戻ったときに、その企業の行が見える位置にする */
let lastOpenedCompany: string | null = null;

/**
 * 契約詳細のパス。戻るときに指定した月と絞り込みを移る前のままにするよう、契約一覧のクエリを list に持たせる。
 * URL に持つので、新しいタブで開いたときや再読み込みの後も、同じ契約一覧に戻れる
 */
const detailPath = (companyId: string, listSearch: string) => {
  const list = listSearch.replace(/^\?/, '');
  const path = `/credit/contracts/${encodeURIComponent(companyId)}`;
  return list ? `${path}?${new URLSearchParams({ list }).toString()}` : path;
};

/**
 * 契約一覧（FKV3-13045）。サイドバー「クレジット制」>「契約管理」。
 * 指定した月について、企業ごとに、契約料金プランとデータコネクトのクレジット数の値を並べる。
 * 表は画面の幅と高さいっぱいに使い、見出しの行を固定して値の行だけをスクロールする。
 */
export function ContractListPage() {
  const s = useContractList();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const data = s.list.status === 'ok' ? s.list.data : null;
  const [monthOpen, setMonthOpen] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);
  // 契約詳細から戻ったときに、見える位置にする企業（「契約一覧へ」は state で、ブラウザの「戻る」は最後に開いた企業）
  const restoreTarget = useRef<string | null>(
    (location.state as { focusCompany?: string } | null)?.focusCompany ??
      (navigationType === 'POP' ? lastOpenedCompany : null),
  );
  // 最後に契約詳細を開いた企業の行は、選択した行の色で示す
  const [opened, setOpened] = useState<string | null>(restoreTarget.current);

  useEffect(() => {
    const target = restoreTarget.current;
    if (!target || s.list.status !== 'ok' || s.refreshing) return;
    restoreTarget.current = null;
    const row = Array.from(
      tableRef.current?.querySelectorAll<HTMLElement>('[data-company-id]') ?? [],
    ).find((e) => e.dataset.companyId === target);
    row?.scrollIntoView?.({ block: 'center' });
  }, [s.list, s.refreshing, s.shown]);

  const markOpened = (companyId: string) => {
    lastOpenedCompany = companyId;
    setOpened(companyId);
  };

  const openDetail = (company: CompanySummary) => {
    markOpened(company.id);
    navigate(detailPath(company.id, location.search));
  };

  // 検索と絞り込みを変えたら、表の先頭から示す。月を変えたときは、同じ企業の並びで見比べられるよう位置を保つ
  const scrollToTop = () => {
    if (tableRef.current) tableRef.current.scrollTop = 0;
  };

  const monthDisabled = !data || s.refreshing;

  return (
    <AppLayout title="契約管理 / 契約一覧" showSearch={false} disableContentPadding>
      <Box display="flex" flexDirection="column" gap={1.5} sx={fillViewportSx}>
        <Box
          display="flex"
          alignItems="center"
          columnGap={3}
          rowGap={1}
          flexWrap="wrap"
          flexShrink={0}
        >
          <Box display="flex" alignItems="center" gap={0.5} role="group" aria-label="月">
            <IconButton
              size="small"
              aria-label="前の月"
              disabled={monthDisabled}
              onClick={() => data && s.selectMonth(addMonths(data.month, -1))}
            >
              <ChevronLeftIcon size={18} />
            </IconButton>
            {/* 表示している月を押すと、月を選ぶ小窓を開く */}
            <Popover open={monthOpen} onOpenChange={setMonthOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-w-[112px] tabular-nums"
                  aria-label={data ? `月を選ぶ（${monthLabel(data.month)}）` : '月を選ぶ'}
                  disabled={monthDisabled}
                >
                  <span aria-live="polite">{data ? monthLabel(data.month) : ''}</span>
                  <CalendarIcon />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-3">
                {data && (
                  <MonthGrid
                    value={data.month}
                    current={data.current_month}
                    onSelect={(month) => {
                      setMonthOpen(false);
                      s.selectMonth(month);
                    }}
                  />
                )}
              </PopoverContent>
            </Popover>
            <IconButton
              size="small"
              aria-label="次の月"
              disabled={monthDisabled}
              onClick={() => data && s.selectMonth(addMonths(data.month, 1))}
            >
              <ChevronRightIcon size={18} />
            </IconButton>
            <Button
              size="sm"
              variant="outline"
              aria-label="当月に戻す"
              disabled={!data || s.refreshing || data.month === data.current_month}
              onClick={() => data && s.selectMonth(data.current_month)}
            >
              当月
            </Button>
          </Box>
          <TextField
            size="small"
            placeholder="企業名・企業ID"
            value={s.query}
            onChange={(e) => {
              s.setQuery(e.target.value);
              scrollToTop();
            }}
            slotProps={{
              htmlInput: { 'aria-label': '企業名か企業IDで絞り込む' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={16} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: 240 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={s.favoritesOnly}
                onChange={(e) => {
                  s.setFavoritesOnly(e.target.checked);
                  scrollToTop();
                }}
              />
            }
            label="お気に入りのみ表示"
          />
          {data && (
            <Typography variant="body2" color="text.secondary" ml="auto">
              {s.shown.length} / {data.companies.length}社
            </Typography>
          )}
        </Box>

        {s.list.status === 'loading' && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}
        {s.list.status === 'error' && (
          <Alert
            severity="error"
            action={
              <Button size="sm" variant="outline" onClick={s.reload}>
                <RefreshCwIcon />
                再読み込み
              </Button>
            }
          >
            {s.list.message}
          </Alert>
        )}
        {data && (
          <ContractTable
            containerRef={tableRef}
            month={data.month}
            companies={s.shown}
            refreshing={s.refreshing}
            opened={opened}
            listSearch={location.search}
            toggling={s.toggling}
            emptyMessage={
              s.favoritesOnly && !data.companies.some((c) => c.favorite)
                ? 'お気に入りの企業はまだありません。☆を押すと登録できます。'
                : '条件に合う企業はありません。'
            }
            onToggleFavorite={(c) => void s.toggleFavorite(c)}
            onOpen={openDetail}
            onOpenedByLink={markOpened}
          />
        )}
      </Box>
    </AppLayout>
  );
}

interface ContractTableProps {
  /** 値の行をスクロールする領域 */
  containerRef: React.RefObject<HTMLDivElement | null>;
  month: string;
  companies: CompanySummary[];
  /** ほかの月を取得中。取得し終えるまで、今の値を薄く示す */
  refreshing: boolean;
  /** 最後に契約詳細を開いた企業 */
  opened: string | null;
  /** 契約詳細から戻るときのための、今の URL のクエリ */
  listSearch: string;
  toggling: ReadonlySet<string>;
  emptyMessage: string;
  onToggleFavorite: (company: CompanySummary) => void;
  onOpen: (company: CompanySummary) => void;
  onOpenedByLink: (companyId: string) => void;
}

const numberCell = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;
const none = (
  <Typography component="span" variant="body2" color="text.secondary">
    なし
  </Typography>
);

// 横スクロールしても動かない列（お気に入り・企業ID・企業名）の幅と位置
const FAVORITE_WIDTH = 48;
const ID_WIDTH = 140;
// 企業名は、画面の幅に余りがあれば長い名前も省略せずに示す
const NAME_WIDTH = { min: 220 };
const NAME_MAX_WIDTH = 400;
// お気に入りの列は幅が狭いので左右の余白を置かず、星を列の中央に置く
const favoriteCol = (header?: boolean) => {
  const cell = stickyCell('left', 0, FAVORITE_WIDTH, { header });
  return { ...cell, sx: { ...(cell.sx as object), px: 0, textAlign: 'center' } };
};
const idCol = (header?: boolean) => stickyCell('left', FAVORITE_WIDTH, ID_WIDTH, { header });
const nameCol = (header?: boolean) =>
  stickyCell('left', FAVORITE_WIDTH + ID_WIDTH, NAME_WIDTH, { header, edge: true });

/**
 * 企業ごとの、指定した月の値の表。行を選ぶと契約詳細に移る。企業名はリンクで、新しいタブでも開ける。
 * 見出しの行を固定して値の行だけをスクロールし、幅が足りないときは、お気に入り・企業ID・企業名を固定して横にスクロールする
 */
function ContractTable({
  containerRef,
  month,
  companies,
  refreshing,
  opened,
  listSearch,
  toggling,
  emptyMessage,
  onToggleFavorite,
  onOpen,
  onOpenedByLink,
}: ContractTableProps) {
  return (
    <Box position="relative" flex="0 1 auto" minHeight={0} display="flex" flexDirection="column">
      {refreshing && (
        <LinearProgress
          aria-label="取得中"
          sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 4 }}
        />
      )}
      <TableContainer
        ref={containerRef}
        component={Paper}
        variant="outlined"
        sx={{ flex: '0 1 auto', minHeight: 0, overflow: 'auto' }}
      >
        <Table
          stickyHeader
          size="small"
          aria-label={`${monthLabel(month)}の契約の値`}
          sx={compactTableSx}
        >
          <TableHead>
            <TableRow>
              <TableCell {...favoriteCol(true)} aria-label="お気に入り">
                {/* 見出しの星と値の行の星は、列の中央にそろえる */}
                <Box display="flex" justifyContent="center">
                  <StarIcon size={14} aria-hidden />
                </Box>
              </TableCell>
              <TableCell {...idCol(true)}>企業ID</TableCell>
              <TableCell {...nameCol(true)}>企業名</TableCell>
              <TableCell sx={fitColumnSx(120)}>料金プラン</TableCell>
              <TableCell sx={fitColumnSx(170)}>期間</TableCell>
              <TableCell sx={{ ...numberCell, ...fitColumnSx(150) }}>
                <HeaderLabel name="契約クレジット数" unit="（クレジット/月）" />
              </TableCell>
              <TableCell sx={{ ...numberCell, ...fitColumnSx(150) }}>
                <HeaderLabel name="クレジット販売単価" unit="（円/クレジット）" />
              </TableCell>
              <TableCell sx={{ ...numberCell, ...fitColumnSx(150) }}>
                <HeaderLabel name="月額料金" unit="（円）" />
              </TableCell>
              <TableCell sx={{ ...numberCell, ...fitColumnSx(150) }}>
                <HeaderLabel name="データコネクト" unit="（クレジット/月）" />
              </TableCell>
              {/* 余った幅を受け取る空の列。値の列を企業名の近くにまとめ、余りは右端に空ける */}
              <TableCell aria-hidden />
            </TableRow>
          </TableHead>
          <TableBody sx={{ opacity: refreshing ? 0.5 : 1 }}>
            {companies.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography variant="body2" color="text.secondary" py={1}>
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {companies.map((c) => {
              const plan = c.pricing_plan;
              return (
                <TableRow
                  key={c.id}
                  hover
                  selected={opened === c.id}
                  data-company-id={c.id}
                  sx={[{ cursor: 'pointer' }, stickyRowSx]}
                  onClick={() => onOpen(c)}
                >
                  <TableCell {...favoriteCol()}>
                    <Box display="flex" justifyContent="center">
                      <IconButton
                        size="small"
                        sx={{ p: 0.25 }}
                        aria-pressed={c.favorite}
                        aria-label={`${c.name}をお気に入り${c.favorite ? 'から外す' : 'に登録する'}`}
                        disabled={toggling.has(c.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleFavorite(c);
                        }}
                      >
                        <StarIcon
                          size={18}
                          className={c.favorite ? 'fill-amber-400 text-amber-500' : 'text-gray-400'}
                        />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell {...idCol()}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {c.id}
                    </Typography>
                  </TableCell>
                  <TableCell {...nameCol()} title={c.name}>
                    <MuiLink
                      component={RouterLink}
                      to={detailPath(c.id, listSearch)}
                      underline="hover"
                      variant="body2"
                      fontWeight="bold"
                      noWrap
                      display="block"
                      maxWidth={NAME_MAX_WIDTH}
                      onClick={(e: React.MouseEvent) => {
                        // 行のクリックで二重に移らないようにする。修飾キーつきのクリックは新しいタブで開く
                        e.stopPropagation();
                        onOpenedByLink(c.id);
                      }}
                    >
                      {c.name}
                    </MuiLink>
                  </TableCell>
                  <TableCell>{plan ? planLabels[plan.pricing_plan] : none}</TableCell>
                  <TableCell>{plan ? formatPeriod(plan) : ''}</TableCell>
                  <TableCell sx={numberCell}>{numberText(plan?.monthly_contract_credit)}</TableCell>
                  <TableCell sx={numberCell}>{numberText(plan?.credit_unit_price)}</TableCell>
                  <TableCell sx={numberCell}>{numberText(plan?.monthly_fee)}</TableCell>
                  <TableCell sx={numberCell}>
                    {c.data_connect_credit
                      ? numberText(c.data_connect_credit.monthly_credit)
                      : none}
                  </TableCell>
                  <TableCell aria-hidden />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
